/**
 * Order note types (PD-14917).
 *
 * `order_note.type` is a varchar holding these numeric strings. The labels
 * used to be hardcoded in five separate places — the order Notes tab, the
 * cart sidebar, the invoice sidebar, the quote sidebar and a skeleton — which
 * is how they drifted apart and how it became unclear which of them a customer
 * could see. They are defined once here instead.
 *
 * The distinction that matters is `isCustomerVisible`. Production content
 * across thirteen tenants shows the three original types are used for internal
 * collaboration — pricing decisions, margin discussion, named competitors,
 * approvals naming individuals, and in one tenant direct criticism of a named
 * employee. None of them may ever reach a customer document. CUSTOMER_FACING
 * exists so there is one channel that can.
 */
export enum OrderNoteType {
	/** Internal working notes. Never shown to a customer. */
	INTERNAL = '1',
	/** Instructions for the internal operations / fulfilment team. */
	OPERATIONS = '2',
	/** Instructions for the driver making the delivery. */
	DELIVERY = '3',
	/** The only note a customer ever sees. Printed on the invoice. */
	CUSTOMER_FACING = '4',
}

export interface OrderNoteTypeDefinition {
	type: OrderNoteType;
	/** Label shown to internal users, in the CRM. */
	label: string;
	/** Short guidance rendered beneath the field at the point of entry. */
	help: string;
	/**
	 * Heading used on customer-facing documents. Only meaningful when
	 * `isCustomerVisible` is true — a customer should not be shown our internal
	 * taxonomy, so the invoice simply calls it "Order Notes".
	 */
	documentHeading?: string;
	isCustomerVisible: boolean;
}

export const ORDER_NOTE_TYPES: ReadonlyArray<OrderNoteTypeDefinition> = [
	{
		type: OrderNoteType.INTERNAL,
		label: 'Order Notes (Internal)',
		help: 'Only visible to your team. Never shown to the customer.',
		isCustomerVisible: false,
	},
	{
		type: OrderNoteType.OPERATIONS,
		label: 'Operations Instructions',
		help: 'Instructions for the operations team handling this order.',
		isCustomerVisible: false,
	},
	{
		type: OrderNoteType.DELIVERY,
		label: 'Delivery Instructions',
		help: 'Shown to the driver at the delivery stop.',
		isCustomerVisible: false,
	},
	{
		type: OrderNoteType.CUSTOMER_FACING,
		label: 'Customer-Facing Note',
		help: 'The customer will see this. Printed on the invoice as "Order Notes".',
		documentHeading: 'Order Notes',
		isCustomerVisible: true,
	},
];

const BY_TYPE = new Map<string, OrderNoteTypeDefinition>(
	ORDER_NOTE_TYPES.map((definition) => [definition.type, definition]),
);

/**
 * Definition for a stored `order_note.type`, or undefined when the value is
 * not one we recognise. Historic rows can carry unexpected values, so callers
 * must handle the undefined case rather than assume.
 */
export function getOrderNoteTypeDefinition(
	type: string | null | undefined,
): OrderNoteTypeDefinition | undefined {
	return type != null ? BY_TYPE.get(type) : undefined;
}

/** Internal label for a stored type, falling back to the raw value. */
export function getOrderNoteLabel(type: string | null | undefined): string {
	return getOrderNoteTypeDefinition(type)?.label ?? `Unknown Type (${type})`;
}

/** True only for the one type a customer is allowed to see. */
export function isCustomerVisibleOrderNote(
	type: string | null | undefined,
): boolean {
	return getOrderNoteTypeDefinition(type)?.isCustomerVisible === true;
}
